<template>
  <div v-if="!isPoppedOut" class="audio-player">
    <div class="player-content">
      <!-- Play Button -->
      <button class="play-button" @click="togglePlay">
        <svg v-if="!isPlaying" viewBox="0 0 24 24" class="play-icon">
          <path d="M8 5v14l11-7z"/>
        </svg>
        <svg v-else viewBox="0 0 24 24" class="pause-icon">
          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
        </svg>
      </button>

      <!-- Track Information -->
      <transition name="fade" mode="out-in">
        <div v-if="track" :key="track.housekeeping?.hid" class="track-info" :class="{ 'fading': isFading }">
          <!-- Column 1 - Track Details -->
          <div class="track-details">
            <!-- Title Row with Rating -->
            <div class="track-row track-title-row">
              <div class="title-rating-container">
                <div class="title-text">
                  <strong>{{ track.work?.title || 'Loading...' }}</strong>
                  <span v-if="track.work?.extra_info" class="track-extra"> - {{ track.work.extra_info }}</span>
                  <span v-if="track.work?.duration" class="track-duration"> [{{ track.work.duration }}]</span>
                </div>

                <!-- Rating Section -->
                <div class="rating-or-auth">
                  <!-- Show two rows of stars if user is signed in -->
                  <div v-if="isUserSignedIn && track.work?.id" class="rating-container">
                    <!-- Row 1: Track's Average Rating (read-only) -->
                    <div class="rating-row average-rating-row">
                      <span class="rating-label">Track Rating:</span>
                      <StarRating
                        :model-value="trackAverageRating"
                        :read-only="true"
                        :small="true"
                        class="track-average-rating"
                      />
                    </div>

                    <!-- Row 2: User's Rating (interactive) -->
                    <div class="rating-row user-rating-row">
                      <span class="rating-label">Your Rating:</span>
                      <StarRating
                        v-model="userRating"
                        @rate="handleRating"
                        :small="true"
                        :orange-hover="true"
                        class="user-interactive-rating"
                      />
                    </div>
                  </div>

                  <!-- Show auth button if not signed in -->
                  <div v-else-if="!isUserSignedIn" class="auth-prompt">
                    <AuthButton @auth-change="handleAuthChange" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Composer -->
            <div class="track-row" v-if="track?.work?.composer">
              Composer:
              <router-link
                v-if="track.work.composerid"
                :to="`/composer/${track.work.composerid}`"
                class="track-link"
              >{{ track.work.composer }}</router-link>
              <span v-else>{{ track.work.composer }}</span>
            </div>

            <!-- Organist -->
            <div class="track-row" v-if="track?.artist?.name">
              Organist:
              <router-link
                v-if="track.artist.artist_id"
                :to="`/artist/${track.artist.artist_id}`"
                class="track-link"
              >{{ track.artist.name }}</router-link>
              <span v-else>{{ track.artist.name }}</span>
            </div>

            <!-- Album -->
            <div class="track-row" v-if="track?.album?.album">
              Album:
              <router-link
                v-if="track.album.album_id"
                :to="`/album/${track.album.album_id}`"
                class="track-link"
              >{{ track.album.album }}</router-link>
              <span v-else>{{ track.album.album }}</span>
            </div>
          </div>

          <!-- Column 2 - Organ Info and Links -->
          <div class="track-links">
            <!-- Organ Information -->
            <div class="track-row" v-if="track?.organ?.instituion">
              {{ track.organ.instituion }}<span v-if="track.organ.city">, {{ track.organ.city }}</span><span v-if="track.organ.state">, {{ track.organ.state }}</span><span v-if="track.organ.country">, {{ track.organ.country }}</span>
              <br>
              <a v-if="track.organ.link" :href="track.organ.link" target="_blank" rel="noopener" class="organ-link">
                <span v-if="track.organ.date">{{ track.organ.date }}</span>&nbsp;<span v-if="track.organ.manuals && track.organ.ranks">{{ track.organ.manuals }}/{{ track.organ.ranks }}</span>&nbsp;<span v-if="track.organ.builder1">{{ track.organ.builder1 }}</span><span v-if="track.organ.builder2">/{{ track.organ.builder2 }}</span>
              </a>
              <span v-else>
                <span v-if="track.organ.date">{{ track.organ.date }}</span> <span v-if="track.organ.manuals && track.organ.ranks">{{ track.organ.manuals }}/{{ track.organ.ranks }}</span> <span v-if="track.organ.builder1">{{ track.organ.builder1 }}</span><span v-if="track.organ.builder2">/{{ track.organ.builder2 }}</span>
              </span>
            </div>

            <!-- Buy Album Link -->
            <div class="track-row" v-if="track?.album?.buycd">
              <a :href="track.album.buycd" target="_blank" rel="noopener" class="track-link">Buy Album</a>
            </div>

            <!-- PDF Sheet Music Link -->
            <div class="track-row" v-if="track?.work?.PDFsheetmusic">
              <a :href="track.work.PDFsheetmusic" target="_blank" rel="noopener" class="track-link">PDF Sheet Music</a>
            </div>

            <!-- Buy Sheet Music Link -->
            <div class="track-row" v-if="track?.work?.Buysheetmusic">
              <a :href="track.work.Buysheetmusic" target="_blank" rel="noopener" class="track-link">Buy Sheet Music</a>
            </div>
          </div>
        </div>
      </transition>

      <!-- Volume Control Column -->
      <div class="volume-column">
        <!-- Pop-out Button (top right) -->
        <button class="popout-button" @click="popOutPlayer" title="Pop out player">
          <svg viewBox="0 0 24 24">
            <path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3m-2 16H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2v7z"/>
          </svg>
        </button>

        <!-- Volume Control (centered) -->
        <div class="volume-control">
          <input type="range" min="0" max="1" step="0.01" v-model="volume" @input="changeVolume" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StarRating from '../components/StarRating.vue'
import AuthButton from '../components/AuthButton.vue'
import { onAuthChange } from '../utils/authUtils.js'
import { rateTrack, getTrackRating } from '../utils/ratingUtils.js'
import { broadcastRatingUpdate } from '../utils/ratingEvents.js'

export default {
  name: 'AudioPlayer',

  components: {
    StarRating,
    AuthButton
  },

  data () {
    return {
      isPlaying: false,
      audio: null,
      volume: 1.0,
      track: null,
      refreshTimeout: null,
      isFading: false,
      streamUrl: '',
      lastHID: null,
      isPoppedOut: false,
      popoutWindow: null,
      popupInitSent: false,
      // Firebase Authentication
      isUserSignedIn: false,
      userRating: 0,
      trackAverageRating: 0,
      authUnsubscribe: null,
      resizeObserver: null
    }
  },

  mounted () {
    this.determineStreamUrl()
    this.fetchNowPlaying()

    // Only stay hidden if popup is actually open
    if (sessionStorage.getItem('playerPoppedOut') === 'true') {
      // Check if popup window actually exists
      const popupExists = window.open('', 'OrganLivePlayer')
      if (popupExists && !popupExists.closed && popupExists.location.href !== 'about:blank') {
        this.isPoppedOut = true
        this.popupInitSent = false // Reset flag for existing popup
        popupExists.close() // Close the test window
        window.open('', 'OrganLivePlayer') // Reopen reference
      } else {
        // Popup doesn't exist, clean up and show player
        sessionStorage.removeItem('playerPoppedOut')
        this.isPoppedOut = false
        this.popupInitSent = false
      }
    }

    // Track actual player height so body padding always clears it
    this.$nextTick(() => {
      const el = this.$el
      if (el && window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(entries => {
          for (const entry of entries) {
            const h = Math.ceil(entry.contentRect.height +
              parseFloat(getComputedStyle(el).paddingTop) +
              parseFloat(getComputedStyle(el).paddingBottom))
            document.documentElement.style.setProperty('--player-actual-height', h + 'px')
          }
        })
        this.resizeObserver.observe(el)
      }
    })

    // Listen for Firebase auth state changes
    this.authUnsubscribe = onAuthChange(async (user) => {
      this.isUserSignedIn = !!user

      if (user) {
        console.log('User signed in:', user.displayName || user.email)

        // Reload user rating when signed in
        if (this.track?.work?.id) {
          this.userRating = await getTrackRating(this.track.work.id)
        }
      } else {
        console.log('User signed out')
        this.userRating = 0
      }
    })
  },

  beforeUnmount () {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout)
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    // Only pause audio if popup is not open (popup owns the audio when open)
    if (this.audio && !this.isPoppedOut) {
      this.audio.pause()
    }
    // Don't close popup window - let it continue independently
    window.removeEventListener('message', this.handlePopupMessage)

    // Clean up auth listener
    if (this.authUnsubscribe) {
      this.authUnsubscribe()
    }
  },

  watch: {
    async track (newTrack) {
      console.log('🎵 AudioPlayer: Track changed')
      console.log('  - Track title:', newTrack?.work?.title)
      console.log('  - work.rating (raw):', newTrack?.work?.rating, typeof newTrack?.work?.rating)

      // Load track's average rating from API (work.rating)
      // IMPORTANT: Rating might be a string, convert to number
      const rawRating = newTrack?.work?.rating || 0
      this.trackAverageRating = parseFloat(rawRating)

      console.log('  - trackAverageRating set to:', this.trackAverageRating, typeof this.trackAverageRating)

      // Load user rating for this track (only if signed in)
      if (newTrack?.work?.id && this.isUserSignedIn) {
        this.userRating = await getTrackRating(newTrack.work.id)
        console.log('  - User rating loaded:', this.userRating)
      } else {
        this.userRating = 0
        console.log('  - No user rating (not signed in or no track ID)')
      }

      // Send track update to popup
      if (this.popoutWindow && !this.popoutWindow.closed) {
        this.popoutWindow.postMessage({
          type: 'track-update',
          data: JSON.parse(JSON.stringify(newTrack))
        }, '*')
      }
    }
  },

  methods: {
    togglePlay () {
      // If popup is open, it owns the audio - just send message
      if (this.isPoppedOut && this.popoutWindow && !this.popoutWindow.closed) {
        this.popoutWindow.postMessage({
          type: this.isPlaying ? 'pause' : 'play'
        }, '*')
        return
      }

      // Otherwise, parent manages audio
      if (!this.audio) {
        this.audio = new Audio(this.streamUrl)
        this.audio.volume = this.volume
      }

      if (this.isPlaying) {
        this.audio.pause()
      } else {
        this.audio.play().catch(err => console.error('Playback failed:', err))
      }

      this.isPlaying = !this.isPlaying
    },

    changeVolume () {
      // If popup is open, send volume change to it
      if (this.isPoppedOut && this.popoutWindow && !this.popoutWindow.closed) {
        this.popoutWindow.postMessage({
          type: 'volume',
          data: this.volume
        }, '*')
      } else if (this.audio) {
        // Otherwise change parent audio volume
        this.audio.volume = this.volume
      }
    },

    fetchNowPlaying () {
      this.isFading = true
      setTimeout(() => {
        const baseUrl = 'https://api.organlive.com/1/playing'
        const url = this.lastHID ? `${baseUrl}/${this.lastHID}` : baseUrl

        fetch(url)
          .then(res => res.json())
          .then(data => {
            if (data.housekeeping?.refresh === 'no') {
              this.scheduleRefresh(data.housekeeping.timeout || 45000)
              this.isFading = false
              return
            }

            this.track = data
            this.lastHID = data.housekeeping?.hid || null

            setTimeout(() => {
              this.isFading = false
            }, 50)

            this.scheduleRefresh(data.housekeeping?.timeout || 45000)
          })
          .catch(err => {
            console.error('Error fetching now playing:', err)
            this.scheduleRefresh(60000)
            this.isFading = false
          })
      }, 300)
    },

    scheduleRefresh (ms) {
      if (this.refreshTimeout) {
        clearTimeout(this.refreshTimeout)
      }
      this.refreshTimeout = setTimeout(() => {
        this.fetchNowPlaying()
      }, ms)
    },

    determineStreamUrl () {
      const offset = new Date().getTimezoneOffset()
      this.streamUrl = offset > 0
        ? 'https://play.organlive.com:7010/320'
        : 'https://play.organlive.com:7010/320'
    },

    popOutPlayer () {
      const width = 500
      const height = 600
      const left = (window.screen.width - width) / 2
      const top = (window.screen.height - height) / 2

      // Capture playing state before pausing
      const wasPlaying = this.isPlaying
      console.log('[Parent] Opening popup, wasPlaying:', wasPlaying, 'hasTrack:', !!this.track)

      // Set up message handler BEFORE opening popup
      window.addEventListener('message', this.handlePopupMessage)

      // Pause parent audio (popup will take over)
      if (this.audio && this.isPlaying) {
        this.audio.pause()
        console.log('[Parent] Paused parent audio')
      }

      this.popoutWindow = window.open(
        `${window.location.origin}/player-popup.html`,
        'OrganLivePlayer',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no`
      )

      if (this.popoutWindow) {
        console.log('[Parent] Popup window opened successfully')
        this.isPoppedOut = true
        sessionStorage.setItem('playerPoppedOut', 'true')

        // Send initial state to popup with longer delay to ensure it's ready
        setTimeout(() => {
          if (!this.popupInitSent) {
            console.log('[Parent] Sending init to popup (timeout)')
            this.sendStateToPopup(wasPlaying)
            this.popupInitSent = true
          }
        }, 500)

        const checkClosed = setInterval(() => {
          if (this.popoutWindow && this.popoutWindow.closed) {
            console.log('[Parent] Popup closed')
            clearInterval(checkClosed)
            this.isPoppedOut = false
            this.popupInitSent = false
            sessionStorage.removeItem('playerPoppedOut')
            window.removeEventListener('message', this.handlePopupMessage)
            this.popoutWindow = null
            // When popup closes, sync state back to parent
            this.isPlaying = false
          }
        }, 500)
      } else {
        console.error('[Parent] Failed to open popup window')
        window.removeEventListener('message', this.handlePopupMessage)
      }
    },

    sendStateToPopup (wasPlaying = false) {
      if (this.popoutWindow && !this.popoutWindow.closed) {
        let plainTrack = null

        // Safely serialize track, handle null case
        if (this.track) {
          try {
            plainTrack = JSON.parse(JSON.stringify(this.track))
          } catch (err) {
            console.error('[Parent] Error serializing track:', err)
            plainTrack = null
          }
        }

        const initData = {
          track: plainTrack,
          isPlaying: wasPlaying,
          volume: this.volume,
          streamUrl: this.streamUrl,
          userRating: this.userRating
        }

        console.log('[Parent] Sending init to popup:', {
          hasTrack: !!plainTrack,
          isPlaying: wasPlaying,
          volume: this.volume,
          streamUrl: this.streamUrl,
          userRating: this.userRating
        })

        try {
          this.popoutWindow.postMessage({
            type: 'init',
            data: initData
          }, '*')
          console.log('[Parent] Init message sent successfully')
        } catch (err) {
          console.error('[Parent] Error sending message to popup:', err)
        }
      } else {
        console.warn('[Parent] Cannot send to popup - window closed or null')
      }
    },

    handlePopupMessage (event) {
      try {
        if (!event.data || !event.data.type) {
          console.warn('[Parent] Invalid message received from popup')
          return
        }

        const { type, data } = event.data
        console.log('[Parent] Received message from popup:', type, data)

        switch (type) {
          case 'ready':
            // Popup is ready - send state immediately if we haven't already
            console.log('[Parent] Popup ready, popupInitSent:', this.popupInitSent)
            if (this.popoutWindow && !this.popoutWindow.closed && !this.popupInitSent) {
              const wasPlaying = this.isPlaying
              console.log('[Parent] Sending init to popup (ready message), wasPlaying:', wasPlaying)
              this.sendStateToPopup(wasPlaying)
              this.popupInitSent = true
            }
            break
          case 'popup-playing':
            // Popup started playing - sync parent state
            console.log('[Parent] Popup is playing')
            this.isPlaying = true
            break
          case 'popup-paused':
            // Popup paused - sync parent state
            console.log('[Parent] Popup is paused')
            this.isPlaying = false
            break
          case 'popup-volume':
            // Popup changed volume - sync parent state
            console.log('[Parent] Popup volume changed:', data)
            this.volume = data
            break
          case 'navigate':
            console.log('[Parent] Navigate to:', data.route)
            if (data.route && this.$router) {
              this.$router.push(data.route)
            }
            break
          case 'rating-updated':
            // Popup rated a track - broadcast to all components
            console.log('[Parent] Rating update from popup:', data)
            if (data.trackId) {
              // Update local track if it matches
              if (this.track && this.track.work && String(this.track.work.id) === String(data.trackId)) {
                this.trackAverageRating = data.averageRating
                this.userRating = data.userRating
                if (this.track.work) {
                  this.track.work.rating = data.averageRating
                }
                console.log('✓ Updated player ratings from popup')
              }
              // Broadcast to all other components
              broadcastRatingUpdate(data.trackId, data.userRating, data.averageRating, data.ratingCount)
            }
            break
        }
      } catch (err) {
        console.error('[Parent] Error handling popup message:', err)
      }
    },

    // Handle rating submission
    async handleRating (rating) {
      // Check if user is signed in
      if (!this.isUserSignedIn) {
        alert('Please sign in to rate tracks')
        return
      }

      // Get track ID from work.id
      const trackId = this.track?.work?.id

      if (!trackId) {
        console.error('No track ID available for rating')
        return
      }

      try {
        const result = await rateTrack(trackId, rating)

        if (result.savedLocally) {
          this.userRating = rating
          console.log('✓ Rating saved:', rating, 'stars')
        }

        if (result.submittedToAPI) {
          console.log('✓ Rating submitted to API successfully')
          console.log('📦 Full API response:', result)
          console.log('📦 result.data:', result.data)

          // Update track average if API returned new value
          // API returns: { success: true, data: { average_rating: 4.5, ... } }
          if (result.data && result.data.data && result.data.data.average_rating !== undefined) {
            const newAverage = result.data.data.average_rating
            const ratingCount = result.data.data.rating_count || 0

            console.log('⭐ BEFORE UPDATE:')
            console.log('  - trackAverageRating:', this.trackAverageRating)
            console.log('  - track.work.rating:', this.track?.work?.rating)

            this.trackAverageRating = newAverage

            console.log('⭐ AFTER UPDATE:')
            console.log('  - trackAverageRating:', this.trackAverageRating)
            console.log('  - newAverage value:', newAverage, typeof newAverage)

            // Also update the track object so it persists
            if (this.track && this.track.work) {
              this.track.work.rating = newAverage
              console.log('  - track.work.rating updated to:', this.track.work.rating)
            }

            // Broadcast rating update to all components
            broadcastRatingUpdate(trackId, rating, newAverage, ratingCount)
            console.log('✓ Rating update broadcasted globally')
          } else {
            console.warn('⚠ API response missing average_rating')
            console.warn('  - result.data:', result.data)
            console.warn('  - result.data.data:', result.data?.data)
            console.warn('  - average_rating field:', result.data?.data?.average_rating)
          }
        } else {
          console.warn('Rating saved locally but API submission failed:', result.errors)
        }
      } catch (error) {
        console.error('Error handling rating:', error)
        alert('Failed to save rating. Please try again.')
      }
    },

    // Handle auth state change from AuthButton
    handleAuthChange (user) {
      this.isUserSignedIn = !!user
      console.log('Auth state changed:', user ? 'signed in' : 'signed out')
    }
  }
}
</script>

<style scoped>
.audio-player {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #003366;
  color: var(--color-white);
  padding: 1rem;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
  z-index: 1000;
}

.player-content {
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.play-button {
  background: var(--color-white);
  border: none;
  width: 50px;
  height: 50px;
  border-radius: var(--radius-round);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background var(--transition-fast);
  flex-shrink: 0;
  order: 1;
}

.play-button:hover {
  background: #f0f0f0;
}

.play-button svg {
  width: 28px;
  height: 28px;
  fill: #003366;
}

.track-info {
  flex: 1;
  display: flex;
  gap: 2rem;
  min-width: 0;
  transition: opacity var(--transition-default);
  order: 2;
}

.track-info.fading {
  opacity: 0.5;
}

.track-details,
.track-links {
  flex: 1;
  min-width: 0;
}

.track-row {
  margin: 0.25rem 0;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.track-title-row {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

/* Title and rating container */
.title-rating-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.title-text {
  flex: 1;
  min-width: 0;
}

.rating-or-auth {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

/* Rating Container - Two Rows */
.rating-container {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.rating-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rating-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  min-width: 85px;
}

/* Distinct styling for each row */
.average-rating-row {
  /* Track average rating - yellow stars */
}

.user-rating-row {
  /* User interactive rating - orange hover */
}

.auth-prompt {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.track-rating {
  /* StarRating component styles */
}

.track-extra {
  font-style: italic;
  font-weight: normal;
}

.track-duration {
  color: rgba(255, 255, 255, 0.8);
  font-weight: normal;
}

.track-link,
.track-links a,
.organ-link {
  color: var(--color-primary);
  text-decoration: none;
}

.track-link:hover,
.track-links a:hover,
.organ-link:hover {
  text-decoration: underline;
}

.volume-column {
  width: 150px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  order: 3;
}

.popout-button {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity var(--transition-fast);
  z-index: 10;
}

.popout-button:hover {
  opacity: 1;
}

.popout-button svg {
  width: 18px;
  height: 18px;
  fill: var(--color-white);
}

.volume-control {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.volume-control input[type="range"] {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.3);
  outline: none;
  -webkit-appearance: none;
}

.volume-control input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: var(--radius-round);
  background: var(--color-primary);
  cursor: pointer;
}

.volume-control input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: var(--radius-round);
  background: var(--color-primary);
  border: none;
  cursor: pointer;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity var(--transition-default);
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive - volume wraps first */
@media (max-width: 1024px) {
  .track-info {
    gap: 1.5rem;
  }
}

@media (max-width: 900px) {
  .volume-column {
    order: 4;
    width: 100%;
    margin-top: 0.5rem;
  }

  .popout-button {
    top: 50%;
    transform: translateY(-50%);
  }

  .title-rating-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .rating-or-auth {
    width: 100%;
  }

  .rating-container {
    width: 100%;
  }

  .rating-label {
    min-width: 80px;
    font-size: 0.8rem;
  }
}

@media (max-width: 768px) {
  .player-content {
    gap: 0.75rem;
  }

  .play-button {
    width: 45px;
    height: 45px;
  }

  .popout-button svg {
    width: 18px;
    height: 18px;
  }

  .track-info {
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }

  .track-row {
    font-size: 0.9rem;
  }

  .track-title-row {
    font-size: 0.95rem;
  }
}

@media (max-width: 480px) {
  .audio-player {
    padding: 0.75rem;
  }

  .player-content {
    gap: 0.5rem;
  }

  .play-button {
    width: 40px;
    height: 40px;
  }

  .play-button svg {
    width: 20px;
    height: 20px;
  }

  .popout-button {
    display: none;
  }

  .track-row {
    font-size: 0.85rem;
  }

  .track-title-row {
    font-size: 0.9rem;
  }

  .volume-column {
    width: 100%;
  }

  .title-rating-container {
    gap: 0.5rem;
  }

  .rating-label {
    min-width: 70px;
    font-size: 0.75rem;
  }
}
</style>
